/**
 * Auth Hook
 */

import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services';
import type { PlatformUser, LoginResponse } from '../types';

export function useAuth() {
  const [user, setUser] = useState<PlatformUser | null>(() =>
    authService.getCurrentUser()
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const isAuthenticated = authService.isAuthenticated();

  useEffect(() => {
    // Sync user state with localStorage
    const storedUser = authService.getCurrentUser();
    if (storedUser && !user) {
      setUser(storedUser);
    }
  }, [user]);

  const login = useCallback(
    async (email: string, password: string) => {
      setIsLoading(true);
      setError(null);

      try {
        const response: LoginResponse = await authService.login({ email, password });
        authService.storeAuthData(response);
        setUser(response.user);
        navigate('/');
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Login failed. Please check your credentials.';
        setError(message);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [navigate]
  );

  const logout = useCallback(() => {
    authService.logout();
    setUser(null);
    navigate('/login');
  }, [navigate]);

  return {
    user,
    isAuthenticated,
    isLoading,
    error,
    login,
    logout,
  };
}
